import { useCallback, useEffect, useMemo, useState } from 'react'
import SystemHeader from '@/widgets/layout/SystemHeader'
import { toCsvString, downloadCsvString } from '@/utils/download'
import { useBackToParent, usePersistentPageSize } from '@/hooks'
import { queriesApi } from '@/services'
import { isHandledAuthError } from '@/services/client'
import type { CsvExportQueryRowResponse } from '@/services/queries/types'
import { CASE_STATUSES, CASE_STATUS_KEYS } from '@/constants/enums'
import useSearchPersistence from '@/hooks/useSearchPersistence'
import { Pagination, BackButton, SearchCard, ResultCard, TableLoadingRow, DemoInput, DemoSelect, DemoBadge, DownloadIcon } from '@/widgets/demo/ListPrimitives';
import { demoButtonClass, computePagination } from '@/widgets/demo/listHelpers';
const BENEFIT_STATUS_OPTIONS = [
  { value: 'ALL', label: '受給状態（全て）' },
  { value: 'ELIGIBLE', label: '受給中' },
  { value: 'SUSPENDED', label: '廃止' },
]

const PAYMENT_STATUS_OPTIONS = [
  { value: 'ALL', label: '案件ステータス（全て）' },
  ...CASE_STATUS_KEYS.map((key) => {
    const label = CASE_STATUSES[key]
    return { value: label, label }
  }),
]

type OutputFieldKey =
  | 'identId'
  | 'householdNo'
  | 'name'
  | 'address'
  | 'benefitStatusLabel'
  | 'benefitAmount'
  | 'paymentStatusLabel'
  | 'applicationDate'

const OUTPUT_FIELD_DEFS: Array<{ key: OutputFieldKey; label: string }> = [
  { key: 'identId', label: '識別ID' },
  { key: 'householdNo', label: '世帯番号' },
  { key: 'name', label: '氏名' },
  { key: 'address', label: '住所' },
  { key: 'benefitStatusLabel', label: '受給状態' },
  { key: 'benefitAmount', label: '給付金額' },
  { key: 'paymentStatusLabel', label: '案件ステータス' },
  { key: 'applicationDate', label: '登録日' },
]

const DEFAULT_FIELDS = OUTPUT_FIELD_DEFS.map((field) => field.key)

// 最後に適用した検索条件・出力項目・ページを localStorage に保持する（顧客要望：
// 「検索条件は最後に選択した結果を保持する」）。RecipientSearch と同じ useSearchPersistence を使用。
interface CsvExportPersisted {
  identId: string
  benefitStatus: string
  paymentStatus: string
  dateFrom: string
  dateTo: string
  selectedFields: OutputFieldKey[]
  page: number
}

const DEFAULT_PERSISTED: CsvExportPersisted = {
  identId: '',
  benefitStatus: 'ALL',
  paymentStatus: 'ALL',
  dateFrom: '',
  dateTo: '',
  selectedFields: DEFAULT_FIELDS,
  page: 1,
}

const CSVExport = () => {
  const goBackToParent = useBackToParent()

  const [savedSearch, setSavedSearch] = useSearchPersistence<CsvExportPersisted>('csv-export', DEFAULT_PERSISTED)

  const [qIdentId, setQIdentId] = useState(savedSearch.identId)
  const [qBenefitStatus, setQBenefitStatus] = useState(savedSearch.benefitStatus)
  const [qPaymentStatus, setQPaymentStatus] = useState(savedSearch.paymentStatus)
  const [qDateFrom, setQDateFrom] = useState(savedSearch.dateFrom)
  const [qDateTo, setQDateTo] = useState(savedSearch.dateTo)
  const [selectedFields, setSelectedFields] = useState<OutputFieldKey[]>(savedSearch.selectedFields ?? DEFAULT_FIELDS)

  const [applied, setApplied] = useState({
    identId: savedSearch.identId,
    benefitStatus: savedSearch.benefitStatus,
    paymentStatus: savedSearch.paymentStatus,
    dateFrom: savedSearch.dateFrom,
    dateTo: savedSearch.dateTo,
  })

  const [page, setPage] = useState(savedSearch.page ?? 1)
  const [pageSize, setPageSize] = usePersistentPageSize('csv-export')
  const [rows, setRows] = useState<CsvExportQueryRowResponse[]>([])
  const [totalElements, setTotalElements] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(false)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await queriesApi.csvExport({
        identId: applied.identId.trim() || undefined,
        benefitStatus: applied.benefitStatus === 'ALL' ? undefined : applied.benefitStatus,
        paymentStatus: applied.paymentStatus === 'ALL' ? undefined : applied.paymentStatus,
        dateFrom: applied.dateFrom || undefined,
        dateTo: applied.dateTo || undefined,
        page: page - 1,
        size: pageSize,
      })
      // 取得ページが範囲外（永続化された page が絞り込み後の総ページ数を超える等）で
      // 0件返却された場合、該当データはあるのに「データなし」表示になる。最終ページへ寄せて再取得する。
      if (data.content.length === 0 && data.totalElements > 0 && page > 1) {
        setTotalElements(data.totalElements)
        setTotalPages(Math.max(1, data.totalPages || 1))
        setPage(Math.max(1, Math.ceil(data.totalElements / pageSize)))
        return
      }
      setRows(data.content)
      setTotalElements(data.totalElements)
      setTotalPages(Math.max(1, data.totalPages || 1))
    } catch (error) {
      setRows([])
      setTotalElements(0)
      setTotalPages(1)
      if (isHandledAuthError(error)) return
    } finally {
      setLoading(false)
    }
  }, [applied, page, pageSize])

  useEffect(() => {
    // 検索条件・ページ変更に応じて一覧を取得する正当なデータ取得 effect。
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchData()
  }, [fetchData])

  const runSearch = () => {
    setLoading(true)
    setPage(1)
    const nextApplied = {
      identId: qIdentId,
      benefitStatus: qBenefitStatus,
      paymentStatus: qPaymentStatus,
      dateFrom: qDateFrom,
      dateTo: qDateTo,
    }
    setApplied(nextApplied)
    setSavedSearch({ ...nextApplied, selectedFields, page: 1 })
  }

  const runClear = () => {
    setLoading(true)
    setQIdentId('')
    setQBenefitStatus('ALL')
    setQPaymentStatus('ALL')
    setQDateFrom('')
    setQDateTo('')
    setPage(1)
    const clearedApplied = {
      identId: '',
      benefitStatus: 'ALL',
      paymentStatus: 'ALL',
      dateFrom: '',
      dateTo: '',
    }
    setApplied(clearedApplied)
    setSavedSearch({ ...clearedApplied, selectedFields, page: 1 })
  }

  const selectedFieldDefs = useMemo(
    () => OUTPUT_FIELD_DEFS.filter((field) => selectedFields.includes(field.key)),
    [selectedFields],
  )

  const rowValue = (row: CsvExportQueryRowResponse, key: OutputFieldKey): string => {
    switch (key) {
      case 'identId':
        return row.identId
      case 'householdNo':
        return row.householdNo
      case 'name':
        return row.name
      case 'address':
        return row.address
      case 'benefitStatusLabel':
        return row.benefitStatusLabel
      case 'benefitAmount':
        return row.benefitAmount != null ? row.benefitAmount.toLocaleString() : ''
      case 'paymentStatusLabel':
        return row.paymentStatusLabel
      case 'applicationDate':
        return row.applicationDate ?? ''
      default:
        return ''
    }
  }

  const downloadAll = async () => {
    try {
      const { data } = await queriesApi.csvExport({
        identId: applied.identId.trim() || undefined,
        benefitStatus: applied.benefitStatus === 'ALL' ? undefined : applied.benefitStatus,
        paymentStatus: applied.paymentStatus === 'ALL' ? undefined : applied.paymentStatus,
        dateFrom: applied.dateFrom || undefined,
        dateTo: applied.dateTo || undefined,
        page: 0,
        size: Math.max(1000, totalElements || 1000),
      })
      const headers = selectedFieldDefs.map((field) => field.label)
      const values = data.content.map((row) => selectedFieldDefs.map((field) => rowValue(row, field.key)))
      const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '')
      downloadCsvString(toCsvString(headers, values), `給付対象者_${stamp}.csv`)
    } catch (error) {
      if (isHandledAuthError(error)) return
    }
  }

  const { safePage, rangeFrom, rangeTo } = computePagination(page, totalElements, pageSize)

  const toggleOutputField = (field: OutputFieldKey) => {
    const next = selectedFields.includes(field)
      ? (selectedFields.length === 1 ? selectedFields : selectedFields.filter((value) => value !== field))
      : [...selectedFields, field]
    setSelectedFields(next)
    setSavedSearch({ ...applied, selectedFields: next, page })
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader breadcrumbs={[{ label: '給付対象者CSV出力' }]} />
      <div className="mx-auto max-w-6xl px-6 py-8">
        <BackButton onClick={goBackToParent} />

        <SearchCard className="mb-6">
          <div className="mb-4 grid grid-cols-2 gap-4 md:grid-cols-5">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">識別ID</label>
              <DemoInput type="text" value={qIdentId} onChange={(e) => setQIdentId(e.target.value)} placeholder="例: SID-U-001234" className="w-full" />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">受給状態</label>
              <DemoSelect value={qBenefitStatus} onChange={(e) => setQBenefitStatus(e.target.value)} className="w-full">
                {BENEFIT_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </DemoSelect>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">案件ステータス</label>
              <DemoSelect value={qPaymentStatus} onChange={(e) => setQPaymentStatus(e.target.value)} className="w-full">
                {PAYMENT_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </DemoSelect>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">登録日（From）</label>
              <DemoInput type="date" value={qDateFrom} onChange={(e) => setQDateFrom(e.target.value)} className="w-full" />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">登録日（To）</label>
              <DemoInput type="date" value={qDateTo} onChange={(e) => setQDateTo(e.target.value)} className="w-full" />
            </div>
          </div>
          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-3">
              <label className="mb-2 block text-sm font-medium text-gray-700">出力項目</label>
              <div className="grid grid-cols-2 gap-2">
                {OUTPUT_FIELD_DEFS.map((field) => (
                  <label key={field.key} className="flex cursor-pointer items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={selectedFields.includes(field.key)}
                      onChange={() => toggleOutputField(field.key)}
                      className="h-4 w-4 rounded text-blue-600"
                    />
                    <span className="text-sm">{field.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="col-span-1 flex items-end justify-end">
              <div className="flex space-x-3">
                <button type="button" onClick={runClear} className={demoButtonClass.secondary}>クリア</button>
                <button type="button" onClick={runSearch} className={demoButtonClass.primary}>検索</button>
              </div>
            </div>
          </div>
        </SearchCard>

        <ResultCard
          className="mb-6"
          title="検索結果"
          extra={
            <>
              <span className="text-sm text-gray-500">該当件数: <span className="font-bold text-blue-600">{totalElements}件</span></span>
              <button type="button" onClick={downloadAll} className={`flex items-center space-x-2 ${demoButtonClass.success}`}>
                <DownloadIcon />
                <span>CSVダウンロード</span>
              </button>
            </>
          }
        >
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full">
              <thead className="border-b border-gray-200 bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">識別ID</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">世帯番号</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">氏名</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">住所</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">受給状態</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">給付金額</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">案件ステータス</th>
                </tr>
              </thead>
              <tbody className={`divide-y divide-gray-200 text-sm transition-opacity ${loading ? 'opacity-60' : ''}`}>
                {rows.length === 0 ? (
                  loading ? (
                    <TableLoadingRow colSpan={7} />
                  ) : (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">該当するデータがありません</td>
                    </tr>
                  )
                ) : (
                  rows.map((row) => (
                    <tr key={`${row.identId}-${row.applicationDate ?? ''}`} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-sm">{row.identId}</td>
                      <td className="px-4 py-3 font-medium">{row.householdNo}</td>
                      <td className="px-4 py-3">{row.name}</td>
                      <td className="px-4 py-3">{row.address}</td>
                      <td className="px-4 py-3"><DemoBadge status={row.benefitStatusLabel} className="py-0.5" /></td>
                      <td className="px-4 py-3 text-left">{row.benefitAmount != null ? row.benefitAmount.toLocaleString() : '－'}円</td>
                      <td className="px-4 py-3"><DemoBadge status={row.paymentStatusLabel} className="py-0.5" /></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination currentPage={safePage} totalPages={totalPages} total={totalElements} rangeFrom={rangeFrom} rangeTo={rangeTo} onPageChange={(next) => { setLoading(true); setPage(next); setSavedSearch({ ...applied, selectedFields, page: next }); }} pageSize={pageSize} onPageSizeChange={(next) => { setLoading(true); setPageSize(next); setPage(1); setSavedSearch({ ...applied, selectedFields, page: 1 }); }} />
          <p className="mt-2 text-xs text-gray-500">* 画面はページ単位で表示しています（CSVは検索結果を全件出力します）</p>
        </ResultCard>
      </div>
    </div>
  )
}

export default CSVExport
